// src/components/stages/IPStage.tsx
// Reached only when the doctor chose Inpatient (IP) as part of Consultation
// (see ConsultStage.tsx) — visit_type is already committed by then, so this
// stage has just one job: assign a bed and create the admission record.
import { useState, useEffect } from 'react';
import { getWards, createAdmission, getDoctors } from '../../api';
import type { StageProps } from '../../types/visit';
import { StageLayout } from '../ui/StageLayout';
import { StageActionBar } from '../ui/StageActionBar';
import { CoveragePanel } from '../../features/claims/CoveragePanel';
import { Button } from '../ui/Button';

interface Bed {
  id: number;
  bed_number: string | number;
  status: string;
}
interface Ward {
  id: number;
  name: string;
  ward_type: string;
  daily_rate: number | string;
  beds: Bed[];
}
interface AvailableBed extends Bed {
  wardName: string;
  wardType: string;
  dailyRate: number | string;
}
interface Doctor {
  id: number;
  full_name: string;
}

// "Other (type name)" lets a locum or visiting doctor who isn't in the
// roster still be recorded — the field stays a free-text value either way.
const OTHER_DOCTOR = '__other__';

const ADMISSION_TYPES = [
  { value: 'emergency', label: 'Emergency' },
  { value: 'elective', label: 'Elective' },
  { value: 'transfer-in', label: 'Transfer-in' },
  { value: 'day-care', label: 'Day-care' },
];

export default function IPStage({ visit, patient, completeStage, loading }: Pick<StageProps, 'visit' | 'patient' | 'completeStage' | 'loading'>) {
  const [wards, setWards] = useState<Ward[] | null>(null);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [bedId, setBedId] = useState('');
  const [admissionType, setAdmissionType] = useState('emergency');
  const [admittingDoctor, setAdmittingDoctor] = useState('');
  const [doctorPick, setDoctorPick] = useState('');
  const [reason, setReason] = useState('');
  const [diagnosis, setDiagnosis] = useState('');
  const [notes, setNotes] = useState('');
  const [admitting, setAdmitting] = useState(false);
  const [error, setError] = useState('');
  // Insurance Claims Phase 4.5.1 (wireframe S1) — coverage needs an
  // admission_id, which doesn't exist until "Confirm admission" below
  // succeeds. So the bed/reason form creates the admission as step 1, then
  // this stage shows the payer capture as step 2, before telling the visit
  // pipeline the IP stage is done.
  const [createdAdmissionId, setCreatedAdmissionId] = useState<number | null>(null);

  useEffect(() => { getWards().then(setWards).catch(() => setWards([])); }, []);
  useEffect(() => { getDoctors().then(setDoctors).catch(() => setDoctors([])); }, []);

  const availableBeds: AvailableBed[] = (wards || []).flatMap(w =>
    w.beds.filter(b => b.status === 'available').map(b => ({ ...b, wardName: w.name, wardType: w.ward_type, dailyRate: w.daily_rate }))
  );

  const finish = () => {
    if (!createdAdmissionId) return;
    completeStage('ip', { visit_type: 'IP', admission_id: createdAdmissionId, bed_id: bedId });
  };

  const confirm = async () => {
    if (!bedId) { setError('Select a bed to admit the patient into.'); return; }
    if (!reason.trim()) { setError('Enter the reason for admission — it drives the Case Sheet\'s clinical snapshot until a full assessment is done.'); return; }
    setError(''); setAdmitting(true);
    try {
      const admission = await createAdmission({
        visit_id: visit.id, patient_id: patient?.id,
        bed_id: bedId, admitting_doctor: admittingDoctor.trim() || null,
        admission_type: admissionType,
        reason_for_admission: reason.trim(),
        provisional_diagnosis: diagnosis.trim() || null, admission_notes: notes.trim() || null,
      });
      setAdmitting(false);
      setCreatedAdmissionId(admission.id);
    } catch (e) {
      setAdmitting(false);
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const missingBed = !bedId;
  const missingReason = !reason.trim();

  if (createdAdmissionId) {
    return (
      <div className="fade-in flex flex-col gap-3.5">
        <StageLayout variant="single">
          <CoveragePanel
            admissionId={createdAdmissionId}
            continueLabel="Save & continue →"
            onSaved={finish}
            onSkip={finish}
          />
        </StageLayout>
        <StageActionBar readiness="Admission created — capture who is paying, then continue." readinessTone="success">
          <Button variant="ghost" onClick={finish}>Skip for now →</Button>
        </StageActionBar>
      </div>
    );
  }

  return (
    <div className="fade-in flex flex-col gap-3.5">
    <StageLayout variant="single">
    <div className="card">
      <div style={{ fontSize:20, fontWeight:700, marginBottom:6 }}>🛏 Ward admission</div>
      <div style={{ fontSize:13, color:'#6B7280', marginBottom:20 }}>{patient?.name || visit?.name} · {visit?.visit_id} — assign a bed to complete the admission.</div>

      <div style={{ marginBottom:14 }}>
        <label className="lbl">Admission type</label>
        <select value={admissionType} onChange={e=>setAdmissionType(e.target.value)}>
          {ADMISSION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>

      {wards === null ? (
        <div style={{ fontSize:12, color:'#9CA3AF', marginBottom:14 }}>Loading available beds…</div>
      ) : (
        <div style={{ marginBottom:14 }}>
          <label className="lbl">Bed *</label>
          <select value={bedId} onChange={e=>setBedId(e.target.value)}>
            <option value="">Select an available bed…</option>
            {availableBeds.map(b => (
              <option key={b.id} value={b.id}>{b.wardName} ({b.wardType}) — Bed {b.bed_number} · ₹{b.dailyRate}/day</option>
            ))}
          </select>
          {availableBeds.length === 0 && <div style={{ fontSize:11.5, color:'#DC2626', marginTop:4 }}>No beds currently available.</div>}
        </div>
      )}

      <div style={{ marginBottom:14 }}>
        <label className="lbl">Reason for admission *</label>
        <textarea value={reason} onChange={e=>setReason(e.target.value)}
          placeholder="Why this patient is being admitted — e.g. severe dehydration requiring IV fluids and monitoring"
          style={{ resize:'vertical', height:52 }} />
        <div style={{ fontSize:11, color:'#9CA3AF', marginTop:3 }}>
          Shown on the Case Sheet's clinical snapshot until a full admission assessment is recorded.
        </div>
      </div>

      <div className="grid2" style={{ marginBottom:14 }}>
        <div>
          <label className="lbl">Admitting doctor</label>
          <select
            value={doctorPick}
            onChange={e => {
              const val = e.target.value;
              setDoctorPick(val);
              setAdmittingDoctor(val === OTHER_DOCTOR ? '' : val);
            }}
          >
            <option value="">Select doctor…</option>
            {doctors.map(d => <option key={d.id} value={d.full_name}>{d.full_name}</option>)}
            <option value={OTHER_DOCTOR}>Other (type name)…</option>
          </select>
          {doctorPick === OTHER_DOCTOR && (
            <input
              value={admittingDoctor}
              onChange={e => setAdmittingDoctor(e.target.value)}
              placeholder="Dr. name"
              style={{ marginTop: 6 }}
            />
          )}
        </div>
        <div>
          <label className="lbl">Provisional diagnosis</label>
          <input value={diagnosis} onChange={e=>setDiagnosis(e.target.value)} placeholder="e.g. Suspected dengue" />
        </div>
      </div>
      <div style={{ marginBottom:20 }}>
        <label className="lbl">Admission notes (optional)</label>
        <textarea value={notes} onChange={e=>setNotes(e.target.value)} style={{ resize:'vertical', height:56 }} />
      </div>

      {error && <div style={{ marginBottom:14, padding:'8px 12px', background:'#FEE2E2', color:'#991B1B', borderRadius:'var(--radius)', fontSize:12 }}>{error}</div>}
    </div>
    </StageLayout>
    <StageActionBar
      readiness={missingBed ? 'Select a bed to continue' : missingReason ? 'Enter the reason for admission to continue' : '✓ Ready to admit'}
      readinessTone={missingBed || missingReason ? 'warn' : 'success'}
    >
      <button className="btn-primary" disabled={loading||admitting} onClick={confirm}>
        {admitting ? 'Admitting…' : 'Confirm admission & continue →'}
      </button>
    </StageActionBar>
    </div>
  );
}
